import React, { useCallback, useMemo } from 'react';
import {
  QLookup,
  QModalHeader,
  QText,
  useCurrentUser as useQCurrentUser,
  useToastProvider,
} from '@qualio/ui-components';
import { useTranslation } from 'react-i18next';

import { Group, ModalComponentProps, User } from '../../types';
import { createErrorToast, createSuccessToast } from '../../utils/toast.utils';
import { useUsers } from '../../hooks/queries/useUsers';
import { useAssignManagers } from '../../hooks/mutations/useAssignManagers';

type AssignManagersModalProps = ModalComponentProps & {
  isOpen: boolean;
  group: Group;
  setRefetch: () => void;
};

export const AssignManagersModal: React.FC<AssignManagersModalProps> = ({ isOpen, closeModal, group, setRefetch }) => {
  const { t } = useTranslation();
  const { showToast } = useToastProvider();
  const { companyId: currentCompanyId } = useQCurrentUser();

  const { data: users = [] } = useUsers({ companyId: currentCompanyId, isOpen });

  const { assignManagers, isAssigning } = useAssignManagers({
    companyId: currentCompanyId,
    groupId: group.id,
  });

  const getUserLookupView = useCallback(
    (): QLookup.DataView<User> => ({
      full_name: {
        header: t('users.table.fullName'),
        width: 'auto',
      },
      email: {
        header: t('users.table.email'),
        width: 'auto',
      },
    }),
    [t],
  );

  const userDataView = useMemo(() => getUserLookupView(), [getUserLookupView]);

  const currentManagersMap = useMemo(
    () =>
      group.managers?.reduce((acc: Map<User['usercompany_id'], User>, manager) => {
        const foundUser = users.find((user) => user.usercompany_id === manager.id);
        if (!foundUser) return acc;
        return acc.set(manager.id, foundUser);
      }, new Map()),
    [group.managers, users],
  );

  const filterUserOptionLookup = useCallback((query: string, user: User, companyGroups: Group[]) => {
    const groupsSearch = companyGroups
      .filter(({ id }) => user?.groups?.some(({ id: groupId }) => groupId === id))
      .map(({ name }) => name);
    return `${user.full_name} ${user.email} ${groupsSearch}`.toLowerCase().includes(query.toLowerCase());
  }, []);

  const filterOptionCallback = useCallback(
    (user: User, searchTerm: string | undefined) => {
      if (!searchTerm) {
        return true;
      }
      return filterUserOptionLookup(searchTerm, user, [group]);
    },
    [group, filterUserOptionLookup],
  );

  const isCurrentManager = useCallback(
    (user: Pick<User, 'usercompany_id'>) => currentManagersMap?.has(user.usercompany_id),
    [currentManagersMap],
  );

  const handleAssignManagers = useCallback(
    async (selectedUsers: readonly User[]) => {
      if (!currentCompanyId) return;

      const managerIdsToAdd = selectedUsers
        .filter((user) => !isCurrentManager(user))
        .map((user) => user.usercompany_id);

      let managerIdsToRemove: number[] = [];
      if (currentManagersMap?.size !== 0) {
        managerIdsToRemove = Array.from(currentManagersMap?.keys() ?? []).filter(
          (id) => !selectedUsers.some((user) => user.usercompany_id === id),
        );
      }
      try {
        return await assignManagers(
          {
            managerIdsToAdd,
            managerIdsToRemove,
          },
          {
            onSuccess: () => {
              showToast(createSuccessToast(t('messages.success.managersAssigned'), 'managers-assign-success-toast'));
              closeModal();
              setRefetch();
            },
          },
        );
      } catch {
        showToast(createErrorToast(t('messages.error.assignManagers'), 'managers-assign-error-toast'));
      }
    },
    [currentCompanyId, group.id, currentManagersMap, assignManagers, showToast, closeModal, setRefetch, t],
  );

  const onSelect = useCallback(
    async (selectedUsers: readonly User[]) => {
      if (!isAssigning) {
        return handleAssignManagers(selectedUsers);
      }
    },
    [handleAssignManagers, isAssigning],
  );

  const isUserCurrentManager = useCallback(
    (user: User) => {
      return Boolean(isCurrentManager(user));
    },
    [isCurrentManager],
  );

  return (
    <QLookup.DataProvider.Fixed data={users} filterOption={filterOptionCallback}>
      {isOpen && (
        <QLookup.MultiSelect<User>
          isOpen={isOpen}
          action={isAssigning ? t('modals.assignManagers.assigning') : t('modals.assignManagers.save')}
          searchPlaceholder={t('modals.assignManagers.searchPlaceholder')}
          accessors={{
            id: 'id',
          }}
          isItemDisabled={() => false}
          isItemPreSelected={isUserCurrentManager}
          onSelect={onSelect}
          onCancel={closeModal}
          view={userDataView}
          defaultSortBy={{
            id: 'full_name',
          }}
        >
          <QModalHeader>
            <QText>{t('modals.assignManagers.title')}</QText>
          </QModalHeader>
        </QLookup.MultiSelect>
      )}
    </QLookup.DataProvider.Fixed>
  );
};
